import { useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { HomeAnnouncements } from './HomeAnnouncements'
import { HomeNotes } from './HomeNotes'
import { NewTasksDrawer } from './NewTasksDrawer'
import { HomeFlightLogCard } from './HomeFlightLogCard'
import { OperationsReviewDocumentsDrawer } from './operations/OperationsReviewDocumentsDrawer'
import { DutyBadge, formatDutyStatus } from './duty-status'
import { ClipboardCheck, FileText, CalendarFold } from '@/components/icons'
import { useDocumentGetExpired, useDocumentGetExpiring } from '../lib/hooks'
import { useRosterGetByCrew } from '../lib/hooks/roster/roster.hooks.queries'
import { useMyDraftTasks } from '../lib/hooks/task'
import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember'
import { useIsPilot } from '../hooks/useIsPilot'
import { hapticLight } from '@/lib/haptics'
import { cn } from '@/lib/utils'

interface HomeContentCardsProps {
  /**
   * Desktop layout: render the My Roster + Review Documents cards side-by-side
   * in a 2-column grid. Mobile (default) stacks them in the vertical card list.
   */
  secondaryTwoColumn?: boolean
}

/**
 * Shared Home card stack — New Tasks, My Roster, Review Documents, Announcements
 * and Notes, plus the task/document review drawers. Rendered inside the mobile
 * `HomeFlightDrawer` (bottom sheet) and the desktop `DesktopHome` content column.
 */
export function HomeContentCards({ secondaryTwoColumn = false }: HomeContentCardsProps) {
  const navigate = useNavigate()
  const { tasks: isTasksDrawerOpen } = useSearch({ from: '/_auth/home' })
  const [isDocsDrawerOpen, setDocsDrawerOpen] = useState(false)
  const { isPilot } = useIsPilot()
  const setTasksDrawerOpen = (open: boolean) =>
    navigate({
      to: '/home',
      search: (prev) => ({ ...prev, tasks: open || undefined }),
    })
  const { data: currentUser } = useCurrentCrewMember()
  const { data: draftTasks } = useMyDraftTasks()
  const { data: expiredDocs } = useDocumentGetExpired(
    currentUser?.id ? { crewId: currentUser.id } : undefined,
  )
  const { data: expiringDocs } = useDocumentGetExpiring(
    currentUser?.id ? { crewId: currentUser.id } : undefined,
  )

  const todayStr = dayjs().format('YYYY-MM-DD')
  const { data: rosterEntries = [] } = useRosterGetByCrew(currentUser?.id ?? '', {
    startDate: todayStr,
    endDate: dayjs().add(90, 'day').format('YYYY-MM-DD'),
  })
  // Current duty status is today's entry; "on until" is the last consecutive
  // day that shares that status.
  const { currentDutyStatus, dutyUntil } = useMemo(() => {
    const byDate = new Map(rosterEntries.map((e) => [e.date, e]))
    const todayEntry = byDate.get(todayStr)
    if (!todayEntry) {
      return { currentDutyStatus: undefined, dutyUntil: undefined }
    }
    let until = todayStr
    let cursor = dayjs(todayStr)
    for (;;) {
      const next = cursor.add(1, 'day').format('YYYY-MM-DD')
      const entry = byDate.get(next)
      if (!entry || entry.dutyStatus !== todayEntry.dutyStatus) break
      until = next
      cursor = cursor.add(1, 'day')
    }
    return { currentDutyStatus: todayEntry.dutyStatus, dutyUntil: until }
  }, [rosterEntries, todayStr])

  // No roster entry for today → treat as Off Duty rather than hiding the section.
  const effectiveDutyStatus = currentDutyStatus ?? 'OffDuty'
  const dutyUntilLabel = dutyUntil ? dayjs(dutyUntil).format('MMM D') : ''
  const rosterSubtitle = currentDutyStatus
    ? currentDutyStatus === 'OnDuty'
      ? `On until ${dutyUntilLabel}`
      : `${formatDutyStatus(currentDutyStatus)} until ${dutyUntilLabel}`
    : formatDutyStatus('OffDuty')

  const newTasksCount = draftTasks?.length ?? 0
  const expiredCount = expiredDocs?.length ?? 0
  const expiringCount = expiringDocs?.length ?? 0
  const docAlertCount = expiredCount + expiringCount
  const invalidDocIds = [...(expiredDocs ?? []), ...(expiringDocs ?? [])].map(
    (doc) => doc.id,
  )
  const docAlertParts: string[] = []
  if (expiredCount > 0) docAlertParts.push(`${expiredCount} expired`)
  if (expiringCount > 0) docAlertParts.push(`${expiringCount} expiring soon`)
  const docAlertSubtitle = docAlertParts.join(' | ')

  // Crew excluded from scheduling have no roster — hide the card entirely.
  const rosterCard = currentUser?.id && currentUser.includeInSchedule !== false && (
    <button
      type="button"
      onClick={() => {
        hapticLight()
        navigate({
          to: '/roster/$crewId',
          params: { crewId: currentUser.id },
        })
      }}
      className="bg-surface-L1 border border-border-L1 rounded-2xl px-4 py-4 shadow-[0px_5px_12.5px_rgba(0,0,0,0.05)] flex items-center justify-between"
    >
      <div className="flex items-center gap-3">
        <div className="bg-background-brand-subtle rounded-lg p-2 flex items-center justify-center">
          <CalendarFold className="w-8 h-8 text-content-brand-bold" />
        </div>
        <div className="flex flex-col items-start">
          <p className="text-base font-semibold text-content-primary-bold leading-6 tracking-[-0.5px]">
            My Roster
          </p>
          <p className="text-sm text-content-primary-subtle leading-5">
            {rosterSubtitle}
          </p>
        </div>
      </div>
      <DutyBadge status={effectiveDutyStatus} />
    </button>
  )

  const docsCard = docAlertCount > 0 && (
    <button
      type="button"
      onClick={() => {
        hapticLight()
        setDocsDrawerOpen(true)
      }}
      className="bg-surface-L1 border border-border-L1 rounded-2xl px-4 py-4 shadow-[0px_5px_12.5px_rgba(0,0,0,0.05)] flex items-center justify-between"
    >
      <div className="flex items-center gap-3">
        <div className="bg-background-brand-subtle rounded-lg p-2 flex items-center justify-center">
          <FileText className="w-8 h-8 text-content-brand-bold" />
        </div>
        <div className="flex flex-col items-start">
          <p className="text-base font-semibold text-content-primary-bold leading-6 tracking-[-0.5px]">
            Review Documents
          </p>
          <p className="text-sm text-content-primary-subtle leading-5">
            {docAlertSubtitle}
          </p>
        </div>
      </div>
      <div className="relative bg-background-brand-subtle rounded-lg px-2 py-1 min-w-6 text-center">
        <span className="text-xs font-medium text-content-primary-bold">
          {docAlertCount}
        </span>
        {expiredCount > 0 && (
          <span className="absolute -top-1 -right-1 size-2.5 rounded-full bg-content-negative-bold" />
        )}
      </div>
    </button>
  )

  return (
    <>
      <HomeFlightLogCard />

      {!isPilot && newTasksCount > 0 && (
        <button
          type="button"
          onClick={() => {
            hapticLight()
            setTasksDrawerOpen(true)
          }}
          className="flex items-center justify-between bg-background-brand-subtle border border-border-brand-bold rounded-2xl px-4 py-4 shadow-[0px_5px_12.5px_rgba(0,0,0,0.05)]"
        >
          <div className="flex items-center gap-3">
            <div className="bg-background-brand-bold rounded-lg p-2 flex items-center justify-center">
              <ClipboardCheck className="w-8 h-8 text-content-primary-inverse" />
            </div>
            <div className="flex flex-col items-start">
              <p className="text-base font-semibold text-content-primary-bold leading-6 tracking-[-0.5px]">
                {newTasksCount === 1 ? 'New Task' : 'New Tasks'}
              </p>
              <p className="text-sm text-content-primary-subtle leading-5">
                {newTasksCount === 1
                  ? '1 new task assigned'
                  : `${newTasksCount} new tasks assigned`}
              </p>
            </div>
          </div>
        </button>
      )}

      {/* My Roster + Review Documents: 2-column on desktop (only when both
          cards render — a lone roster card spans the full width), stacked on
          mobile. */}
      <div
        className={cn(
          secondaryTwoColumn && docsCard
            ? 'grid grid-cols-2 gap-4 [&>*]:min-w-0'
            : 'contents',
        )}
      >
        {rosterCard}
        {docsCard}
      </div>

      <HomeAnnouncements />

      <HomeNotes />

      <NewTasksDrawer
        open={!!isTasksDrawerOpen}
        onOpenChange={setTasksDrawerOpen}
      />
      <OperationsReviewDocumentsDrawer
        open={isDocsDrawerOpen}
        onOpenChange={setDocsDrawerOpen}
        documentIds={invalidDocIds}
      />
    </>
  )
}
